<template>
  <div v-if="home" class="Home" :class="{ blur: me.onboarding && !isWidget }">
    <div style="background-color: #000;">
      <div class="mainWrapper">
        <navBar></navBar>
      </div>
    </div>
    <div v-if="home.embedded_video" class="mainWrapper">
      <div class="LiveVideo">
        <div class="LiveVideo__video">
          <div class="playerWrapper livePlayer" :style="homeCover">
            <template v-show="livePlayerDisplay">
              <mute :muted="muted" @toggleMute="toggleMute()"/>
              <live
                v-if="home.embedded_video.live_badge_shown"
                :position="home.embedded_video.live_badge_position"
              />
              <PlayPause
                v-if="livePlayerDisplay && livePlayerInstance && home.embedded_video.video_type === 'live'"
                :player-state="livePlayerState"
                :player-instance="livePlayerInstance"
              />
              <Fullscreen v-if="livePlayerDisplay"></Fullscreen>
              <div class="playerOffsetter">
                <video-player
                  class="video-player-box liveInstance"
                  ref="liveVideoPlayer"
                  :options="livePlayerOptions"
                  @ready="playerReadied($event)"
                  @play="onLivePlayerPlay($event)"
                  @pause="onLivePlayerPause($event)"
                  @ended="onLivePlayerEnded($event)"
                  @timeupdate="onLiveTimeUpdate($event)"
                >
                </video-player>
              </div>
            </template>
          </div>
        </div>
        <div class="LiveVideo__info infoColumn">
          <div class="infoColumn__current">
            <div class="infoColumn__current__text">
              <div class="infoColumn__current__text__title">{{ home.embedded_video.title }}</div>
              <div class="infoColumn__current__text__body" v-html="nl2br(home.embedded_video.description)" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mainWrapper">
      <template v-if="home.featured_videos">
        <categoryCarousel
          v-for="category in home.featured_videos"
          :category="category"
          :aspectRatio="'16_9'"
          :perPage="`xs-${mClass}slide-2 md-${mClass}slide-4 ${mobile}`"
          :key="category.title"
          ga="Main Featured Videos"
        ></categoryCarousel>
      </template>
      <template v-if="home.category_videos">
        <categoryCarousel
          v-for="category in home.category_videos"
          :category="category"
          :aspectRatio="'1_1'"
          :perPage="`xs-${mClass}slide-3 md-${mClass}slide-5 ${mobile}`"
          :key="category.title"
          :seeAll="true"
          ga="Main Category Videos"
        ></categoryCarousel>
      </template>
      <template v-if="home.categories">
        <categories
          :categories="home.categories"
        ></categories>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-redeclare,global-require,import/no-dynamic-require */
/* eslint max-len: ["error", { "code": 150 }] */

import {
  mapState, mapMutations, mapActions, mapGetters,
} from 'vuex';
import isRetina from 'is-retina';
import MobileDetect from 'mobile-detect';
import get from 'lodash.get';
import nl2br from 'nl2br';
import navBar from './NavBar.vue';
import categoryCarousel from './CategoryCarousel2.vue';
import categories from './Categories.vue';
import mute from './glyphs/Mute.vue';
import live from './glyphs/Live.vue';
import PlayPause from './glyphs/PlayPause.vue';
import Fullscreen from './glyphs/Fullscreen.vue';

export default {
  name: 'Home',
  props: {
    hid: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      nl2br,
      fullscreen: false,
      hls: null,
      playerMenu: true,
      muted: true,
      homeCover: false,
      playerSize: {
        height: 0,
        width: 0,
      },
      retina: isRetina(),
    };
  },
  methods: {
    ...mapActions([
      'enqueueNestedCovers',
      'enqueueImage',
      'processEnqueuedImages',
      'setHomeData',
      'getMe',
      'getPlans',
      'getHome',
      'playVideoById',
    ]),
    ...mapMutations([
      'onPlayerEnded',
      'onPlayerPause',
      'playerStateChanged',
      'onPlayerPlay',
      'appReady',
      'setMd',
      'removeFromQueue',
      'setLivePlayerInstance',
      'onLivePlayerEnded',
      'onLivePlayerPause',
      'onLivePlayerPlay',
      'onLiveTimeUpdate',
      'onLiveSeeked',
      'livePlayerStateChanged',
      'displayLivePlayer',
      'setLoginInfo',
      'redirectToIP',
      'setNavTitle',
      'resetAnimation',
    ]),
    login() {
      this.redirectToIP({
        router: this.$router,
        registered: true,
        ls: this.$ls,
      });
    },
    getToken(ls) {
      const status = ls.get('storageAccessData');
      if (status && status.token) {
        return status.token;
      }
      this.redirectToIP({
        router: this.$router,
        registered: false,
      });
      return true;
    },
    processHomeImages() {
      if (get(this, 'home.embedded_video', false)) {
        if (this.getLPI !== null) {
          const liveVideoStream = get(this, 'home.embedded_video.stream_hls', false);
          if (liveVideoStream) {
            this.playLiveVideo({
              url: liveVideoStream,
              muted: true,
            });
          }
        }
      }
      this.appReady(true);
      window.ga('tok.send', 'screenview', { screenName: 'Main', appName: 'web' });
    },
    toggleMute() {
      const state = this.getLPI.muted();
      this.getLPI.muted(!state);
      this.muted = !state;
      const videoId = get(this, 'home.embedded_video.video_id', '');
      if (this.muted) {
        window.ga('tok.send', 'event', 'Main Embedded Video', 'Video Muted', videoId);
      } else {
        window.ga('tok.send', 'event', 'Main Embedded Video', 'Video Unmuted', videoId);
      }
    },
    playerReadied(player) {
      this.displayLivePlayer(true);
      try {
        const liveCover = get(this, 'home.embedded_video.cover', false);
        if (liveCover) {
          const scaledLiveCover = this.scaleImage(liveCover);
          player.poster(scaledLiveCover);
        }
      } catch (e) {
        this.$log.error(e);
      }
      this.setLivePlayerInstance(player);
      player.muted(true);
      this.hls = player.tech({ IWillNotUseThisInPlugins: true }).hls;
    },
    playLiveVideo(config) {
      this.livePlayerOptions.sources[0].src = config.url;
      this.livePlayerOptions.poster = config.cover;
    },
  },
  beforeMount() {
    this.setNavTitle('');
    this.$ls.remove('widgetMode');
  },
  mounted() {
    if (this.hid) {
      this.$ls.set('singleH', this.hid, 5 * 1000);
    }
    const hid = this.$ls.get('singleH');
    this.$store.state.singleH = hid;
    this.$store.state.nowPlaying = hid;

    if (!/login\?status=/.test(window.location.href)) {
      const that = this;
      const status = this.getCookie(`${window.TOK_NS}storageAccessData`);
      if (status) {
        that.setLoginInfo(status);
        if (!this.me) {
          this.getMe()
            .then(() => {
              if (get(that, 'me.active_offers', false)) {
                window.ga('tok.set', { dimension7: get(that, 'me.active_offers9', []).join(',') });
              }
              that.getPlans();
            })
            .catch((error) => {
              window.app.$log.error(error);
            });
        }
      } else {
        return that.redirectToIP({
          router: that.$router,
          registered: false,
          ls: that.$ls,
        });
      }
    }

    if (this.$route.name === 'clean') {
      this.setHomeData(false);
      this.$ls.remove('storageHomeData');
      this.$router.push('/');
    }

    if (this.$store.state.singleH) {
      this.loadTheHighlight();
    }

    this.resetAnimation();
    const that = this;
    const md = new MobileDetect(window.navigator.userAgent);
    this.setMd(md);

    if (!this.home) {
      const lsHomeData = this.$ls.get('storageHomeData', false);
      if (lsHomeData) {
        this.setHomeData(lsHomeData)
          .then(() => {
            that.processHomeImages();
          })
          .catch((error) => {
            that.$log.error(error);
          });
      } else {
        this.getHome(that.$ls)
          .then(() => {
            that.processHomeImages();
          })
          .catch(() => {
            that.$log.info('Unable to retrive Home data');
          });
      }
    } else {
      that.processHomeImages();
    }
    const na = this.$ls.get('nextAction');
    const np = this.$ls.get('nextPayload');
    if (na && np) {
      if (get(this, 'me.registered', false)) {
        this[na]({
          ...np,
          router: this.$router,
        });
      }
    }
    return true;
  },
  components: {
    categoryCarousel,
    categories,
    navBar,
    mute,
    live,
    PlayPause,
    Fullscreen,
  },
  computed: {
    ...mapActions(['loadTheHighlight']),
    ...mapGetters(['scaleImage', 'getLPI']),
    ...mapState([
      'baseUrl',
      'player',
      'highlights',
      'playlist',
      'plays',
      'players',
      'categories',
      'imageQueue',
      'loading',
      'api',
      'home',
      'livePlayerDisplay',
      'livePlayerOptions',
      'livePlayerInstance',
      'livePlayerState',
      'loginInfo',
      'me',
      'plans',
      'locale',
      'appName',
      'nextAction',
      'nextPayload',
    ]),
    mClass() {
      return this.md.mobile() ? 'm-' : '';
    },
    mobile() {
      return this.md.mobile() ? 'mobile' : '';
    },
  },
};
</script>
<style lang="scss">
.vjs-tech {
  pointer-events: none;
}
.LiveVideo{
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-wrap: wrap;
  &__video{
    flex: 0 0 100%;
    @media (min-width: 768px) {
      flex: 0 0 60%;
    }
  }
  &__info{
    display: none;
    @media (min-width: 768px) {
      display: flex;
      flex: 0 0 40%;
    }
  }
}
.Home {
  -webkit-transition: -webkit-filter 500ms linear;
  filter: blur(0);
  &.blur {
    filter: blur(5px);
  }
}
</style>
